<script setup>
import { computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { toast } from '@/components/ui/toast'
import ReportView from '@/components/report/ReportView.vue'

const route = useRoute()
const router = useRouter()
const store = useStore()

const orgId = computed(() => route.params.org_id)
const reportUuid = computed(() => route.params.report_uuid)
const versionQuery = computed(() => route.query.version ? parseInt(route.query.version, 10) : null)

const currentReport = computed(() => {
  if (!reportUuid.value) return null
  return store.getters['report/getReport'](orgId.value, reportUuid.value)
})

// Fetch report with optional version
const fetchReportWithVersion = async (uuid, version = null) => {
  try {
    await store.dispatch('report/fetchReport', {
      orgId: orgId.value,
      reportUuid: uuid,
      version: version
    })
  } catch (error) {
    console.error('Error fetching report:', error)
    toast({
      title: 'Error',
      description: 'Failed to load report',
      variant: 'destructive'
    })
  }
}

// Watch for report UUID changes
watch(reportUuid, async (newUuid) => {
  if (newUuid) {
    store.dispatch('report/setActiveReport', newUuid)
    store.dispatch('report/setActiveVersion', versionQuery.value)
    await fetchReportWithVersion(newUuid, versionQuery.value)
  }
}, { immediate: true })

// Watch for version query param changes
watch(versionQuery, async (newVersion, oldVersion) => {
  if (reportUuid.value && newVersion !== oldVersion) {
    store.dispatch('report/setActiveVersion', newVersion)
    await fetchReportWithVersion(reportUuid.value, newVersion)
  }
})

const handleReportUpdated = (updatedReport) => {
  store.dispatch('report/updateReport', {
    orgId: orgId.value,
    report: updatedReport
  })
}
</script>

<template>
  <div class="h-full">
    <ReportView
      v-if="currentReport"
      :report="currentReport"
      :organization-id="Number(orgId)"
      @report-updated="handleReportUpdated"
    />
    <div v-else class="h-full flex items-center justify-center text-muted-foreground">
      <p>Loading report...</p>
    </div>
  </div>
</template>
